<template>
  <div class="row fade-in">
    <!-- List of Agents (Left Side) -->
    <div :class="selectedAgent ? 'col-xl-5 col-lg-6' : 'col-xl-12'">
      <div class="card">
        <div class="card-header align-items-center d-flex border-0 pb-0 bg-transparent">
          <h4 class="card-title mb-0 flex-grow-1 fw-bold text-primary">
            <i class="ri-robot-line align-bottom me-2"></i>Emma AI Configuration
          </h4>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="col-12"
              :class="selectedAgent ? 'col-md-12' : 'col-md-6 col-xxl-4'"
            >
              <div
                class="card border shadow-sm transition-all agent-card"
                :class="{ 'border-primary bg-soft-primary-light': selectedAgent && selectedAgent.id === agent.id }"
                style="cursor: pointer;"
                @click="selectAgent(agent)"
              >
                <div class="card-body p-4">
                  <div class="d-flex align-items-start mb-3">
                    <div class="flex-grow-1">
                      <h5 class="fs-15 fw-bold mb-1 text-dark">{{ agent.display_name }}</h5>
                      <span class="badge bg-soft-secondary text-secondary fs-10 uppercase">
                        {{ agent.provider }}
                      </span>
                      <span
                        v-if="agent.model_name && agent.model_name !== 'n/a'"
                        class="badge bg-soft-info text-info fs-10 ms-1"
                      >
                        {{ agent.model_name }}
                      </span>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="form-check form-switch form-switch-right form-switch-md">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          :checked="agent.is_active"
                          @change.stop="toggleAgentStatus(agent, $event)"
                          style="cursor: pointer;"
                        />
                      </div>
                    </div>
                  </div>
                  <p class="text-muted text-truncate-two-lines fs-13 mb-0" style="min-height: 38px;">
                    {{ agent.description || 'No description provided.' }}
                  </p>
                  
                  <div class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top border-light">
                    <span
                      class="badge fs-11"
                      :class="agent.is_active ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'"
                    >
                      <i class="ri-checkbox-blank-circle-fill fs-8 align-middle me-1"></i>
                      {{ agent.is_active ? 'Active' : 'Inactive' }}
                    </span>
                    <button class="btn btn-sm btn-link text-primary p-0 fw-semibold">
                      Configure <i class="ri-arrow-right-s-line align-middle ms-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuration Panel (Right Side) -->
    <div v-if="selectedAgent" class="col-xl-7 col-lg-6 fade-in">
      <div class="card border border-primary-subtle shadow-lg">
        <div class="card-header align-items-center d-flex bg-light border-bottom">
          <div class="flex-grow-1">
            <h5 class="card-title mb-1 fw-bold text-dark">
              <i class="ri-settings-line align-bottom me-2 text-primary"></i>Configure: {{ selectedAgent.display_name }}
            </h5>
            <p class="text-muted mb-0 fs-12">Modify agent instructions, parameters and specific integrations.</p>
          </div>
          <button type="button" class="btn-close" @click="selectedAgent = null"></button>
        </div>
        
        <div class="card-body p-4" style="max-height: calc(100vh - 280px); overflow-y: auto;">
          <!-- Name and Description -->
          <div class="row g-3 mb-4">
            <div class="col-md-12">
              <label class="form-label fw-semibold">Agent Name</label>
              <input type="text" class="form-control" v-model="editForm.display_name" placeholder="Agent Display Name" />
            </div>
            <div class="col-md-12">
              <label class="form-label fw-semibold">Description</label>
              <textarea class="form-control" rows="2" v-model="editForm.description" placeholder="Agent Description"></textarea>
            </div>
          </div>

          <!-- Active Status Switch -->
          <div class="p-3 bg-light rounded-3 d-flex align-items-center mb-4 border">
            <div class="flex-grow-1">
              <h6 class="mb-0 fw-bold">Agent Active Status</h6>
              <p class="text-muted mb-0 fs-12">Enable or disable this AI agent's automated workflows.</p>
            </div>
            <div class="form-check form-switch form-switch-md">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                v-model="editForm.is_active"
              />
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div :class="(editForm.communication_mode === 'voice' || editForm.communication_mode === 'both') ? 'col-md-6' : 'col-md-12'">
              <label class="form-label fw-semibold">Communication Mode</label>
              <select class="form-select" v-model="editForm.communication_mode">
                <option value="sms">Text Message (SMS)</option>
                <option value="voice">Voice Call (AI Agent)</option>
                <option value="both">Both (SMS + Voice)</option>
              </select>
            </div>
            
            <div v-if="editForm.communication_mode === 'voice' || editForm.communication_mode === 'both'" class="col-md-6 fade-in">
              <label class="form-label fw-semibold">Voice ID</label>
              <input type="text" class="form-control" v-model="editForm.voice_id" placeholder="e.g. 248be419-c632-4f23-adf1-5324ed7dbf1d" />
              <div class="form-text text-muted fs-11 mt-1">ID of the voice to be used in voice calls.</div>
            </div>
          </div>

          <!-- Greetings Section (Voice only) -->
          <div v-if="editForm.communication_mode === 'voice' || editForm.communication_mode === 'both'" class="row g-3 mb-4 fade-in">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Inbound Greeting</label>
              <textarea class="form-control" rows="2" v-model="editForm.inbound_greeting" placeholder="Thank you for calling DMB, how can I help you?"></textarea>
              <div class="form-text text-muted fs-11 mt-1">First phrase the agent says when a client calls.</div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Outbound Greeting</label>
              <textarea class="form-control" rows="2" v-model="editForm.outbound_greeting" placeholder="Hello, this is Emma from DMB calling..."></textarea>
              <div class="form-text text-muted fs-11 mt-1">First phrase the agent says when dialing out.</div>
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Agent Workflow Type</label>
              <select class="form-select" v-model="editForm.agent_type">
                <option value="interactive">Interactive (Send & Wait for Response)</option>
                <option value="campaign">Campaign (Send Only / One-way)</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Twilio Sender Number</label>
              <input
                type="text"
                class="form-control"
                v-model="editForm.twilio_phone_number"
                placeholder="e.g. +13462205981"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Twilio Webhook URL</label>
              <div class="input-group">
                <input
                  type="text"
                  class="form-control"
                  :value="webhookUrl"
                  readonly
                  style="font-size: 0.85rem;"
                />
                <button class="btn btn-outline-secondary" type="button" @click="copyWebhookUrl" title="Copy to clipboard">
                  <i class="mdi mdi-content-copy"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Target Entity</label>
              <select class="form-select" v-model="editForm.target_type" @change="onTargetTypeChange">
                <option value="prospect">Prospect (Leads / Prospective Clients)</option>
                <option value="client">Client</option>
                <option value="agent">Agent (Recruits / Candidates / Internal Users)</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Filter by Source</label>
              <div class="custom-multiselect-dropdown dropdown" data-dropdown-type="source">
                <button
                  class="form-select text-start position-relative pe-4"
                  type="button"
                  @click="toggleDropdown('source')"
                >
                  <span class="text-truncate d-inline-block w-100">
                    {{ getSelectedLabels('source') }}
                  </span>
                </button>
                <div
                  class="dropdown-menu p-3 shadow-lg w-100 border-light"
                  :class="{ 'show': dropdownsOpen.source }"
                  style="max-height: 320px; overflow-y: auto; position: absolute; z-index: 1050; border-radius: 8px;"
                >
                  <div class="input-group input-group-sm mb-2">
                    <span class="input-group-text bg-light border-end-0">
                      <i class="ri-search-line text-muted"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0"
                      placeholder="Search source..."
                      v-model="searchQueries.source"
                    />
                    <button
                      v-if="searchQueries.source"
                      class="btn btn-outline-secondary border-start-0"
                      type="button"
                      @click="searchQueries.source = ''"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                  <div class="d-flex justify-content-between fs-11 mb-2 pb-2 border-bottom border-light">
                    <a href="javascript:void(0);" class="text-primary fw-semibold" @click="selectAll('source')">Select All</a>
                    <a href="javascript:void(0);" class="text-muted fw-semibold" @click="clearAll('source')">Clear All</a>
                  </div>
                  <div class="options-container" style="max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="src in filteredSources"
                      :key="src.id"
                      class="form-check dropdown-item py-1 px-2 rounded text-wrap d-flex align-items-center mb-0"
                      style="cursor: pointer;"
                      @click.stop="toggleOption('source', src.id)"
                    >
                      <input
                        class="form-check-input me-2 mt-0"
                        type="checkbox"
                        :checked="editForm.target_source_id.includes(src.id)"
                        style="cursor: pointer;"
                      />
                      <label class="form-check-label w-100 text-dark mb-0 fs-13" style="cursor: pointer;">
                        {{ src.name }}
                      </label>
                    </div>
                    <div v-if="filteredSources.length === 0" class="text-muted text-center fs-12 py-2">
                      No matches found
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-semibold">Filter by Status</label>
              <div class="custom-multiselect-dropdown dropdown" data-dropdown-type="status">
                <button
                  class="form-select text-start position-relative pe-4"
                  type="button"
                  @click="toggleDropdown('status')"
                >
                  <span class="text-truncate d-inline-block w-100">
                    {{ getSelectedLabels('status') }}
                  </span>
                </button>
                <div
                  class="dropdown-menu p-3 shadow-lg w-100 border-light"
                  :class="{ 'show': dropdownsOpen.status }"
                  style="max-height: 320px; overflow-y: auto; position: absolute; z-index: 1050; border-radius: 8px;"
                >
                  <div class="input-group input-group-sm mb-2">
                    <span class="input-group-text bg-light border-end-0">
                      <i class="ri-search-line text-muted"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0"
                      placeholder="Search status..."
                      v-model="searchQueries.status"
                    />
                    <button
                      v-if="searchQueries.status"
                      class="btn btn-outline-secondary border-start-0"
                      type="button"
                      @click="searchQueries.status = ''"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                  <div class="d-flex justify-content-between fs-11 mb-2 pb-2 border-bottom border-light">
                    <a href="javascript:void(0);" class="text-primary fw-semibold" @click="selectAll('status')">Select All</a>
                    <a href="javascript:void(0);" class="text-muted fw-semibold" @click="clearAll('status')">Clear All</a>
                  </div>
                  <div class="options-container" style="max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="stat in filteredStatuses"
                      :key="stat.id"
                      class="form-check dropdown-item py-1 px-2 rounded text-wrap d-flex align-items-center mb-0"
                      style="cursor: pointer;"
                      @click.stop="toggleOption('status', stat.id)"
                    >
                      <input
                        class="form-check-input me-2 mt-0"
                        type="checkbox"
                        :checked="editForm.target_status_id.includes(stat.id)"
                        style="cursor: pointer;"
                      />
                      <label class="form-check-label w-100 text-dark mb-0 fs-13" style="cursor: pointer;">
                        {{ stat.name }}
                      </label>
                    </div>
                    <div v-if="filteredStatuses.length === 0" class="text-muted text-center fs-12 py-2">
                      No matches found
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Scope Filters (Department / Recruiter) -->
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Limit to Department</label>
              <div class="custom-multiselect-dropdown dropdown" data-dropdown-type="department">
                <button
                  class="form-select text-start position-relative pe-4"
                  type="button"
                  @click="toggleDropdown('department')"
                >
                  <span class="text-truncate d-inline-block w-100">
                    {{ getSelectedLabels('department') }}
                  </span>
                </button>
                <div
                  class="dropdown-menu p-3 shadow-lg w-100 border-light"
                  :class="{ 'show': dropdownsOpen.department }"
                  style="max-height: 320px; overflow-y: auto; position: absolute; z-index: 1050; border-radius: 8px;"
                >
                  <div class="input-group input-group-sm mb-2">
                    <span class="input-group-text bg-light border-end-0">
                      <i class="ri-search-line text-muted"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0"
                      placeholder="Search department..."
                      v-model="searchQueries.department"
                    />
                    <button
                      v-if="searchQueries.department"
                      class="btn btn-outline-secondary border-start-0"
                      type="button"
                      @click="searchQueries.department = ''"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                  <div class="d-flex justify-content-between fs-11 mb-2 pb-2 border-bottom border-light">
                    <a href="javascript:void(0);" class="text-primary fw-semibold" @click="selectAll('department')">Select All</a>
                    <a href="javascript:void(0);" class="text-muted fw-semibold" @click="clearAll('department')">Clear All</a>
                  </div>
                  <div class="options-container" style="max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="dept in filteredDepartments"
                      :key="dept.id"
                      class="form-check dropdown-item py-1 px-2 rounded text-wrap d-flex align-items-center mb-0"
                      style="cursor: pointer;"
                      @click.stop="toggleOption('department', dept.id)"
                    >
                      <input
                        class="form-check-input me-2 mt-0"
                        type="checkbox"
                        :checked="editForm.limit_to_department_id.includes(dept.id)"
                        style="cursor: pointer;"
                      />
                      <label class="form-check-label w-100 text-dark mb-0 fs-13" style="cursor: pointer;">
                        {{ dept.name }}
                      </label>
                    </div>
                    <div v-if="filteredDepartments.length === 0" class="text-muted text-center fs-12 py-2">
                      No matches found
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-text fs-11 text-muted">If selected, the agent will only process records belonging to this department.</div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">Limit to Agent / Recruiter</label>
              <div class="custom-multiselect-dropdown dropdown" data-dropdown-type="recruiter">
                <button
                  class="form-select text-start position-relative pe-4"
                  type="button"
                  @click="toggleDropdown('recruiter')"
                >
                  <span class="text-truncate d-inline-block w-100">
                    {{ getSelectedLabels('recruiter') }}
                  </span>
                </button>
                <div
                  class="dropdown-menu p-3 shadow-lg w-100 border-light"
                  :class="{ 'show': dropdownsOpen.recruiter }"
                  style="max-height: 320px; overflow-y: auto; position: absolute; z-index: 1050; border-radius: 8px;"
                >
                  <div class="input-group input-group-sm mb-2">
                    <span class="input-group-text bg-light border-end-0">
                      <i class="ri-search-line text-muted"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0"
                      placeholder="Search recruiter..."
                      v-model="searchQueries.recruiter"
                    />
                    <button
                      v-if="searchQueries.recruiter"
                      class="btn btn-outline-secondary border-start-0"
                      type="button"
                      @click="searchQueries.recruiter = ''"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                  <div class="d-flex justify-content-between fs-11 mb-2 pb-2 border-bottom border-light">
                    <a href="javascript:void(0);" class="text-primary fw-semibold" @click="selectAll('recruiter')">Select All</a>
                    <a href="javascript:void(0);" class="text-muted fw-semibold" @click="clearAll('recruiter')">Clear All</a>
                  </div>
                  <div class="options-container" style="max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="rec in filteredRecruiters"
                      :key="rec.email"
                      class="form-check dropdown-item py-1 px-2 rounded text-wrap d-flex align-items-center mb-0"
                      style="cursor: pointer;"
                      @click.stop="toggleOption('recruiter', rec.email)"
                    >
                      <input
                        class="form-check-input me-2 mt-0"
                        type="checkbox"
                        :checked="editForm.limit_to_user_email.includes(rec.email)"
                        style="cursor: pointer;"
                      />
                      <label class="form-check-label w-100 text-dark mb-0 fs-13" style="cursor: pointer;">
                        {{ rec.name }} ({{ rec.email }})
                      </label>
                    </div>
                    <div v-if="filteredRecruiters.length === 0" class="text-muted text-center fs-12 py-2">
                      No matches found
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-text fs-11 text-muted">If selected, the agent will only process records created by or assigned to this recruiter.</div>
            </div>
          </div>

          <!-- Assigned Tools -->
          <div class="row g-3 mb-4">
            <div class="col-md-12">
              <label class="form-label fw-semibold">
                <i class="ri-tools-line align-bottom me-1"></i>Assigned Tools
              </label>
              <div class="custom-multiselect-dropdown dropdown" data-dropdown-type="tools">
                <button
                  class="form-select text-start position-relative pe-4"
                  type="button"
                  @click="toggleDropdown('tools')"
                >
                  <span class="text-truncate d-inline-block w-100">
                    {{ getSelectedLabels('tools') }}
                  </span>
                </button>
                <div
                  class="dropdown-menu p-3 shadow-lg w-100 border-light"
                  :class="{ 'show': dropdownsOpen.tools }"
                  style="max-height: 320px; overflow-y: auto; position: absolute; z-index: 1050; border-radius: 8px;"
                >
                  <div class="input-group input-group-sm mb-2">
                    <span class="input-group-text bg-light border-end-0">
                      <i class="ri-search-line text-muted"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control border-start-0"
                      placeholder="Search tools..."
                      v-model="searchQueries.tools"
                    />
                  </div>
                  <div class="d-flex justify-content-between fs-11 mb-2 pb-2 border-bottom border-light">
                    <a href="javascript:void(0);" class="text-primary fw-semibold" @click="selectAll('tools')">Select All</a>
                    <a href="javascript:void(0);" class="text-muted fw-semibold" @click="clearAll('tools')">Clear All</a>
                  </div>
                  <div class="options-container" style="max-height: 200px; overflow-y: auto;">
                    <div
                      v-for="tool in filteredTools"
                      :key="tool.id"
                      class="form-check dropdown-item py-1 px-2 rounded text-wrap d-flex align-items-start mb-0"
                      style="cursor: pointer;"
                      @click.stop="toggleOption('tools', tool.id)"
                    >
                      <input
                        class="form-check-input me-2 mt-1"
                        type="checkbox"
                        :checked="editForm.tools.includes(tool.id)"
                        style="cursor: pointer;"
                      />
                      <label class="form-check-label w-100 text-dark mb-0 fs-13" style="cursor: pointer;">
                        {{ tool.name }}
                        <div class="text-muted fs-11">{{ tool.description }}</div>
                      </label>
                    </div>
                    <div v-if="filteredTools.length === 0" class="text-muted text-center fs-12 py-2">
                      No matches found
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-text fs-11 text-muted">Capabilities (function-calling tools) this agent can use during a conversation, whether by SMS or voice.</div>
            </div>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Scheduling Mode</label>
              <select class="form-select" v-model="editForm.schedule_type">
                <option value="interval">Interval (Every X Minutes)</option>
                <option value="cron">Specific Schedule (Cron Expression)</option>
                <option value="disabled">Disabled / Reactive Only (No Schedule)</option>
              </select>
            </div>
            
            <div class="col-md-6" v-if="editForm.schedule_type === 'interval'">
              <label class="form-label fw-semibold">Execution Frequency (Minutes)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="editForm.run_interval_minutes"
                min="0"
                placeholder="e.g. 60"
              />
              <div class="form-text fs-11 text-muted">Use 0 for reactive/webhook-only execution.</div>
            </div>

            <div class="col-12" v-if="editForm.schedule_type === 'cron'">
              <div class="card bg-light border p-3 mb-0">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold text-dark">Cron Expression</label>
                    <input
                      type="text"
                      class="form-control font-monospace"
                      v-model="editForm.schedule_cron"
                      placeholder="* * * * *"
                    />
                    <div class="form-text fs-11 text-muted mt-1">
                      Format: <code>minute hour day_of_month month day_of_week</code>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold text-dark mb-2">Preset Schedules</label>
                    <div class="d-flex flex-wrap gap-1">
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary fs-11 py-1 px-2"
                        @click="editForm.schedule_cron = '0 8 * * 1-5'"
                      >
                        Mon-Fri 8 AM
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary fs-11 py-1 px-2"
                        @click="editForm.schedule_cron = '0 9 * * 1-5'"
                      >
                        Mon-Fri 9 AM
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary fs-11 py-1 px-2"
                        @click="editForm.schedule_cron = '0 8 * * *'"
                      >
                        Every Day 8 AM
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary fs-11 py-1 px-2"
                        @click="editForm.schedule_cron = '0 * * * *'"
                      >
                        Hourly
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary fs-11 py-1 px-2"
                        @click="editForm.schedule_cron = '*/15 * * * *'"
                      >
                        Every 15 Mins
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Model & Provider (if OpenAI supported) -->
          <div class="row g-3 mb-4" v-if="selectedAgent.model_name !== 'n/a'">
            <div class="col-md-6">
              <label class="form-label fw-semibold">AI Provider</label>
              <select class="form-select" v-model="editForm.provider" @change="onProviderChange">
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
                <option value="anthropic">Anthropic Claude</option>
              </select>
              <div class="form-text fs-11 text-muted" v-if="editForm.communication_mode === 'voice' || editForm.communication_mode === 'both'">
                Voice calls only support OpenAI models today, regardless of this setting.
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-semibold">LLM Model</label>
              <select class="form-select" v-model="editForm.model_name">
                <option v-for="m in modelOptionsForProvider" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
          </div>

          <!-- Temperature Slider (for LLM agents) -->
          <div class="mb-4" v-if="selectedAgent.model_name !== 'n/a'">
            <div class="d-flex justify-content-between mb-1">
              <label class="form-label fw-semibold mb-0">Creativity / Temperature ({{ editForm.temperature }})</label>
              <span class="text-muted fs-11" v-if="editForm.temperature <= 0.3">Precise & Safe</span>
              <span class="text-muted fs-11" v-else-if="editForm.temperature <= 0.7">Balanced</span>
              <span class="text-muted fs-11" v-else>Creative</span>
            </div>
            <input
              type="range"
              class="form-range"
              min="0"
              max="1"
              step="0.1"
              v-model.number="editForm.temperature"
            />
          </div>

          <!-- System Prompt & Instructions -->
          <div class="mb-4">
            <label class="form-label fw-semibold d-flex justify-content-between">
              <span>System Prompt & Instructions</span>
              <span class="text-info fs-11" v-if="selectedAgent.name === 'knowledge_base_twilio'"> recruitment base of knowledge is here </span>
            </label>
            <textarea
              class="form-control font-monospace fs-13"
              rows="12"
              v-model="editForm.system_prompt"
              placeholder="Enter system prompt instructions..."
            ></textarea>
            <div class="form-text fs-11 text-muted mt-2">
              <span class="fw-semibold text-secondary">Available Placeholders:</span>
              <div v-if="selectedAgent.name === 'followup_agents'" class="mt-1">
                <code>{contact_name}</code> (First name), <code>{contact_state}</code> (State),
                <code>{is_licensed}</code> (License status), <code>{notes}</code> (Recruiter notes),
                <code>{history}</code> (Chat history), <code>{interview_link}</code> (Calendar link),
                <code>{language_name}</code> (English/Spanish), <code>{opt_out_notice}</code> (STOP message).
              </div>
              <div v-else-if="selectedAgent.name === 'indeed_applicants_sms'" class="mt-1">
                <code>{firstName}</code> (First name), <code>{lastName}</code> (Last name),
                <code>{calendar_link}</code> (Interview scheduling link), <code>{department}</code> (Department name),
                <code>{sourceName}</code> (Source origin), <code>{state}</code> (Target state).
              </div>
              <div v-else class="mt-1">
                <code>{firstName}</code> (First name), <code>{lastName}</code> (Last name),
                <code>{calendar_link}</code> (Calendar link), <code>{department}</code> (Department),
                <code>{sourceName}</code> (Source), <code>{state}</code> (State).
              </div>
            </div>
          </div>

          <!-- Specific Extra Settings (JSON Fields rendered beautifully) -->
          <div class="border-top pt-4 mt-4" v-if="hasExtraSettings">
            <h6 class="fw-bold mb-3 text-secondary">
              <i class="ri-code-s-slash-line align-bottom me-1"></i>Integration & Extra Settings
            </h6>

            <!-- Calendar Link & Twilio Sender settings -->
            <div class="row g-3 mb-3" v-if="editForm.extra_settings && ('calendar_link' in editForm.extra_settings || 'twilio_sender' in editForm.extra_settings)">
              <div class="col-12" v-if="'calendar_link' in editForm.extra_settings">
                <label class="form-label fw-semibold">Calendar Scheduling Link</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editForm.extra_settings.calendar_link"
                  placeholder="Leave empty to use recruiter's department link"
                />
                <div class="form-text fs-11 text-muted">
                  If left empty, the engine dynamically generates and sends the recruiter's department-specific scheduling link.
                </div>
              </div>
              <div class="col-md-6" v-if="'twilio_sender' in editForm.extra_settings">
                <label class="form-label fw-semibold">Twilio Sender Number Override</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editForm.extra_settings.twilio_sender"
                  placeholder="e.g. +13462205981"
                />
              </div>
            </div>

            <!-- Agent Daily SMS Limit & Test Phone -->
            <div class="row g-3 mb-3" v-if="editForm.extra_settings">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Agent Daily Interaction Limit</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="editForm.extra_settings.daily_limit"
                  placeholder="e.g. 500 (leave empty or 0 for no limit)"
                />
                <div class="form-text fs-11 text-muted">
                  Limits the daily SMS messages and/or Voice Calls this specific agent can perform.
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Limit to Specific Phone Numbers (Testing)</label>
                <input
                  type="text"
                  class="form-control border-info"
                  v-model="editForm.extra_settings.limit_to_phone"
                  placeholder="e.g. 8322246105 (comma separated)"
                />
                <div class="form-text fs-11 text-info fw-semibold">
                  If populated, the agent will ONLY process prospects with these exact phone numbers.
                </div>
              </div>
            </div>

            <!-- Outbound Limit Settings -->
            <div class="row g-3 mb-3" v-if="editForm.extra_settings && 'max_outbound_sms' in editForm.extra_settings">
              <div class="col-md-4">
                <label class="form-label fw-semibold">Max Unanswered SMS</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="editForm.extra_settings.max_outbound_sms"
                  placeholder="e.g. 4"
                />
                <div class="form-text fs-11 text-muted">Limit of consecutive unanswered messages before taking action.</div>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Action on Max Reached</label>
                <select class="form-select" v-model="editForm.extra_settings.action_on_max_sms">
                  <option value="stop_sending">Stop Sending (Ignore)</option>
                  <option value="move_status">Move to Status</option>
                  <option value="do_nothing">Do Nothing (Keep Sending)</option>
                </select>
              </div>
              <div class="col-md-4" v-if="editForm.extra_settings.action_on_max_sms === 'move_status'">
                <label class="form-label fw-semibold">Target Status</label>
                <select class="form-select" v-model.number="editForm.extra_settings.action_status_id">
                  <option :value="null">-- Default (No Interesado) --</option>
                  <option v-for="st in availableStatuses" :key="st.id" :value="st.id">
                    {{ st.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Licensed Agents Campaign specific fields -->
            <div class="row g-3" v-if="selectedAgent.name === 'licensed_agents_sms'">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Default Twilio Phone Number</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editForm.extra_settings.twilio_sender_default"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Florida Twilio Phone Number</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editForm.extra_settings.twilio_sender_fl"
                />
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Sequence Messages Templates (1 to 5)</label>
                <div
                  v-for="(tpl, idx) in editForm.extra_settings.templates"
                  :key="idx"
                  class="mb-3 p-3 bg-light rounded border position-relative"
                >
                  <label class="form-label fs-12 fw-bold text-dark mb-1">Message Segment #{{ idx + 1 }}</label>
                  <button type="button" class="btn btn-sm btn-link text-danger position-absolute top-0 end-0 p-1" @click="removeTemplateMessage(idx)" title="Remove Segment">
                    <i class="ri-close-line fs-14"></i>
                  </button>
                  <textarea
                    class="form-control fs-12"
                    rows="3"
                    v-model="editForm.extra_settings.templates[idx]"
                  ></textarea>
                </div>
                <button type="button" class="btn btn-sm btn-soft-primary fw-semibold" @click="addTemplateMessage">
                  <i class="ri-add-line align-middle me-1"></i> Add Segment
                </button>
              </div>
            </div>

            <!-- Fallback templates (Interactive sequence) -->
            <div class="row g-3 mt-2" v-if="editForm.agent_type === 'interactive' && editForm.extra_settings.fallback_templates">
              <div class="col-12">
                <h6 class="fs-12 fw-bold text-dark mb-2">Fallback Template Sequence (Spanish)</h6>
                <div
                  v-for="(tpl, idx) in editForm.extra_settings.fallback_templates.es"
                  :key="'es-' + idx"
                  class="mb-3 p-2 bg-light rounded border position-relative"
                >
                  <label class="form-label fs-11 fw-bold mb-1">Message Segment #{{ idx + 1 }}</label>
                  <button type="button" class="btn btn-sm btn-link text-danger position-absolute top-0 end-0 p-1 mt-1 me-1" @click="removeFallbackMessage('es', idx)" title="Remove Segment">
                    <i class="ri-close-line fs-14"></i>
                  </button>
                  <textarea
                    class="form-control fs-12"
                    rows="3"
                    v-model="editForm.extra_settings.fallback_templates.es[idx]"
                  ></textarea>
                </div>
                <button type="button" class="btn btn-sm btn-soft-primary fw-semibold" @click="addFallbackMessage('es')">
                  <i class="ri-add-line align-middle me-1"></i> Add Spanish Segment
                </button>
              </div>
              <div class="col-12 border-top pt-3">
                <h6 class="fs-12 fw-bold text-dark mb-2">Fallback Template Sequence (English)</h6>
                <div
                  v-for="(tpl, idx) in editForm.extra_settings.fallback_templates.en"
                  :key="'en-' + idx"
                  class="mb-3 p-2 bg-light rounded border position-relative"
                >
                  <label class="form-label fs-11 fw-bold mb-1">Message Segment #{{ idx + 1 }}</label>
                  <button type="button" class="btn btn-sm btn-link text-danger position-absolute top-0 end-0 p-1 mt-1 me-1" @click="removeFallbackMessage('en', idx)" title="Remove Segment">
                    <i class="ri-close-line fs-14"></i>
                  </button>
                  <textarea
                    class="form-control fs-12"
                    rows="3"
                    v-model="editForm.extra_settings.fallback_templates.en[idx]"
                  ></textarea>
                </div>
                <button type="button" class="btn btn-sm btn-soft-primary fw-semibold" @click="addFallbackMessage('en')">
                  <i class="ri-add-line align-middle me-1"></i> Add English Segment
                </button>
              </div>
            </div>

          </div>
        </div>

        <div class="card-footer border-top bg-light d-flex justify-content-end gap-2 p-3">
          <button type="button" class="btn btn-soft-info btn-sm fw-semibold" @click="openTestChat">
            <i class="ri-chat-3-line align-middle me-1"></i>Test Chat
          </button>
          <button type="button" class="btn btn-soft-secondary btn-sm fw-semibold" @click="selectedAgent = null">
            Cancel
          </button>
          <button type="button" class="btn btn-primary btn-sm fw-semibold" @click="saveAgentSettings" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <i v-else class="ri-save-line align-middle me-1"></i>Save Configuration
          </button>
        </div>
      </div>
    </div>

    <!-- Test Chat Modal -->
    <div
      v-if="testChatModalOpen"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0, 0, 0, 0.5); z-index: 1050;"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg bg-body-tertiary">
          <div class="modal-header border-bottom py-3 px-4">
            <h5 class="modal-title fw-bold">Test Agent: {{ selectedAgent?.display_name }}</h5>
            <button type="button" class="btn-close" @click="closeTestChat"></button>
          </div>
          <div class="modal-body p-4" style="height: 400px; display: flex; flex-direction: column;">
            
            <div class="chat-thread flex-grow-1" style="overflow-y: auto; padding: 4px 10px; margin-bottom: 15px; border: 1px solid #e9ebec; border-radius: 8px; background: #fff;">
              <div v-if="testMessages.length === 0" class="text-center text-muted mt-4 fs-13">
                Send a message to start simulating the chat using current unsaved settings.
              </div>
              <div
                v-for="(msg, idx) in testMessages"
                :key="idx"
                class="d-flex mb-3 mt-2"
                :class="msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'"
              >
                <!-- AI -->
                <div
                  v-if="msg.role === 'ai'"
                  style="max-width: 75%; background: #f1f3f4; color: #212529; border-radius: 16px 16px 16px 4px; padding: 10px 14px; font-size: 0.85rem;"
                >
                  <div class="fw-semibold mb-1" style="font-size:0.7rem; color: #5f6368;">Agent</div>
                  <div style="white-space: pre-wrap;">{{ msg.content }}</div>
                </div>

                <!-- User -->
                <div
                  v-else
                  style="max-width: 75%; background: linear-gradient(135deg, #405189, #0ab39c); color: #fff; border-radius: 16px 16px 4px 16px; padding: 10px 14px; font-size: 0.85rem;"
                >
                  <div class="fw-semibold mb-1" style="font-size:0.7rem; opacity:.8;">You</div>
                  <div style="white-space: pre-wrap;">{{ msg.content }}</div>
                </div>
              </div>
              
              <div v-if="testChatLoading" class="d-flex mb-3 justify-content-start mt-2">
                 <div style="background: #f1f3f4; border-radius: 16px 16px 16px 4px; padding: 10px 14px;">
                    <span class="spinner-grow spinner-grow-sm text-primary" role="status" aria-hidden="true"></span>
                    <span class="spinner-grow spinner-grow-sm text-primary ms-1" role="status" aria-hidden="true"></span>
                    <span class="spinner-grow spinner-grow-sm text-primary ms-1" role="status" aria-hidden="true"></span>
                 </div>
              </div>
            </div>

            <div class="d-flex gap-2">
              <input 
                type="text" 
                class="form-control" 
                v-model="testMessageInput" 
                @keyup.enter="sendTestMessage" 
                placeholder="Type your test message..." 
                :disabled="testChatLoading"
              />
              <button class="btn btn-primary" @click="sendTestMessage" :disabled="testChatLoading || !testMessageInput.trim()">
                <i class="ri-send-plane-fill"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "AIAgentSettings",
  data() {
    return {
      agents: [],
      loading: false,
      selectedAgent: null,
      editForm: {
        is_active: true,
        provider: "openai",
        model_name: "gpt-4o-mini",
        temperature: 0.5,
        system_prompt: "",
        target_type: "prospect",
        target_source_id: [],
        target_status_id: [],
        twilio_phone_number: "",
        agent_type: "interactive",
        run_interval_minutes: 60,
        schedule_type: "interval",
        schedule_cron: "* * * * *",
        limit_to_department_id: [],
        limit_to_user_email: [],
        tools: [],
        extra_settings: {}
      },
      availableSources: [],
      availableStatuses: [],
      availableDepartments: [],
      availableRecruiters: [],
      availableTools: [],
      availableModels: {},
      saving: false,
      dropdownsOpen: {
        source: false,
        status: false,
        department: false,
        recruiter: false,
        tools: false
      },
      searchQueries: {
        source: "",
        status: "",
        department: "",
        recruiter: "",
        tools: ""
      },
      testChatModalOpen: false,
      testMessages: [],
      testMessageInput: "",
      testChatLoading: false
    };
  },
  computed: {
    webhookUrl() {
      if (!this.selectedAgent) return "";
      let origin = window.location.origin;
      if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
        origin = 'https://[YOUR_NGROK_DOMAIN]';
      }
      return `${origin}/api/comm/receiveSMS/${this.selectedAgent.name}`;
    },
    hasExtraSettings() {
      if (!this.selectedAgent) return false;
      return Object.keys(this.editForm.extra_settings || {}).length > 0;
    },
    filteredSources() {
      if (!this.searchQueries.source) return this.availableSources;
      const q = this.searchQueries.source.toLowerCase();
      return this.availableSources.filter(x => String(x.name || '').toLowerCase().includes(q));
    },
    filteredStatuses() {
      if (!this.searchQueries.status) return this.availableStatuses;
      const q = this.searchQueries.status.toLowerCase();
      return this.availableStatuses.filter(x => String(x.name || '').toLowerCase().includes(q));
    },
    filteredDepartments() {
      if (!this.searchQueries.department) return this.availableDepartments;
      const q = this.searchQueries.department.toLowerCase();
      return this.availableDepartments.filter(x => String(x.name || '').toLowerCase().includes(q));
    },
    filteredRecruiters() {
      if (!this.searchQueries.recruiter) return this.availableRecruiters;
      const q = this.searchQueries.recruiter.toLowerCase();
      return this.availableRecruiters.filter(x =>
        String(x.name || '').toLowerCase().includes(q) ||
        String(x.email || '').toLowerCase().includes(q)
      );
    },
    filteredTools() {
      if (!this.searchQueries.tools) return this.availableTools;
      const q = this.searchQueries.tools.toLowerCase();
      return this.availableTools.filter(x => String(x.name || '').toLowerCase().includes(q));
    },
    modelOptionsForProvider() {
      return this.availableModels[this.editForm.provider] || [];
    }
  },
  methods: {
    async loadAgents() {
      this.loading = true;
      const data = {
        token: localStorage.getItem("jwt")
      };

      await axios
        .post("/api/ia/agents", data)
        .then(response => {
          this.agents = response.data || [];
        })
        .catch(error => {
          console.error("Error loading AI agents:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          } else {
            Swal.fire("Error", "Could not load AI agents configurations.", "error");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async loadTools() {
      await axios.post("/api/ia/tools", { token: localStorage.getItem("jwt") })
        .then(response => {
          this.availableTools = (response.data || []).filter(t => t.is_active);
        })
        .catch(error => {
          console.error("Error loading tools:", error);
        });
    },
    async loadAvailableModels() {
      await axios.post("/api/ia/available-models", { token: localStorage.getItem("jwt") })
        .then(response => {
          this.availableModels = response.data || {};
        })
        .catch(error => {
          console.error("Error loading available models:", error);
        });
    },
    onProviderChange() {
      const options = this.modelOptionsForProvider;
      if (options.length > 0 && !options.some(m => m.value === this.editForm.model_name)) {
        this.editForm.model_name = options[0].value;
      }
    },
    parseCommaSeparated(val, isInteger = true) {
      if (val === null || val === undefined) return [];
      const strVal = String(val).trim();
      if (!strVal || strVal === 'n/a') return [];
      return strVal.split(',')
        .map(x => x.trim())
        .filter(Boolean)
        .map(x => isInteger ? parseInt(x, 10) : x)
        .filter(x => !isInteger || !isNaN(x));
    },
    toggleDropdown(type) {
      this.dropdownsOpen[type] = !this.dropdownsOpen[type];
      // Close other dropdowns
      for (const key in this.dropdownsOpen) {
        if (key !== type) {
          this.dropdownsOpen[key] = false;
        }
      }
    },
    toggleOption(type, value) {
      let list;
      if (type === 'source') {
        list = this.editForm.target_source_id;
      } else if (type === 'status') {
        list = this.editForm.target_status_id;
      } else if (type === 'department') {
        list = this.editForm.limit_to_department_id;
      } else if (type === 'recruiter') {
        list = this.editForm.limit_to_user_email;
      } else if (type === 'tools') {
        list = this.editForm.tools;
      }

      const idx = list.indexOf(value);
      if (idx === -1) {
        list.push(value);
      } else {
        list.splice(idx, 1);
      }
    },
    selectAll(type) {
      if (type === 'source') {
        this.editForm.target_source_id = this.availableSources.map(x => x.id);
      } else if (type === 'status') {
        this.editForm.target_status_id = this.availableStatuses.map(x => x.id);
      } else if (type === 'department') {
        this.editForm.limit_to_department_id = this.availableDepartments.map(x => x.id);
      } else if (type === 'recruiter') {
        this.editForm.limit_to_user_email = this.availableRecruiters.map(x => x.email);
      } else if (type === 'tools') {
        this.editForm.tools = this.availableTools.map(x => x.id);
      }
    },
    clearAll(type) {
      if (type === 'source') {
        this.editForm.target_source_id = [];
      } else if (type === 'status') {
        this.editForm.target_status_id = [];
      } else if (type === 'department') {
        this.editForm.limit_to_department_id = [];
      } else if (type === 'recruiter') {
        this.editForm.limit_to_user_email = [];
      } else if (type === 'tools') {
        this.editForm.tools = [];
      }
    },
    getSelectedLabels(type) {
      if (type === 'source') {
        const selected = this.editForm.target_source_id;
        if (!selected || selected.length === 0) return "-- All Sources --";
        return selected
          .map(id => {
            const item = this.availableSources.find(x => x.id === id);
            return item ? item.name : id;
          })
          .join(', ');
      } else if (type === 'status') {
        const selected = this.editForm.target_status_id;
        if (!selected || selected.length === 0) return "-- All Statuses --";
        return selected
          .map(id => {
            const item = this.availableStatuses.find(x => x.id === id);
            return item ? item.name : id;
          })
          .join(', ');
      } else if (type === 'department') {
        const selected = this.editForm.limit_to_department_id;
        if (!selected || selected.length === 0) return "-- All Departments --";
        return selected
          .map(id => {
            const item = this.availableDepartments.find(x => x.id === id);
            return item ? item.name : id;
          })
          .join(', ');
      } else if (type === 'recruiter') {
        const selected = this.editForm.limit_to_user_email;
        if (!selected || selected.length === 0) return "-- All Recruiters --";
        return selected
          .map(email => {
            const item = this.availableRecruiters.find(x => x.email === email);
            return item ? item.name : email;
          })
          .join(', ');
      } else if (type === 'tools') {
        const selected = this.editForm.tools;
        if (!selected || selected.length === 0) return "-- No Tools Assigned --";
        return selected
          .map(id => {
            const item = this.availableTools.find(x => x.id === id);
            return item ? item.name : id;
          })
          .join(', ');
      }
      return "";
    },
    handleDocumentClick(event) {
      const isDropdownClick = event.target.closest(".custom-multiselect-dropdown");
      if (!isDropdownClick) {
        this.dropdownsOpen.source = false;
        this.dropdownsOpen.status = false;
        this.dropdownsOpen.department = false;
        this.dropdownsOpen.recruiter = false;
        this.dropdownsOpen.tools = false;
      } else {
        const currentType = isDropdownClick.getAttribute("data-dropdown-type");
        for (const key in this.dropdownsOpen) {
          if (key !== currentType) {
            this.dropdownsOpen[key] = false;
          }
        }
      }
    },
    selectAgent(agent) {
      this.selectedAgent = agent;
      this.editForm = {
        display_name: agent.display_name || "",
        description: agent.description || "",
        is_active: agent.is_active,
        communication_mode: agent.communication_mode || "sms",
        voice_id: agent.voice_id || "",
        inbound_greeting: agent.inbound_greeting || "",
        outbound_greeting: agent.outbound_greeting || "",
        provider: agent.provider || "openai",
        model_name: agent.model_name || "gpt-4o-mini",
        temperature: agent.temperature !== undefined ? agent.temperature : 0.5,
        system_prompt: agent.system_prompt || "",
        target_type: agent.target_type || "prospect",
        target_source_id: this.parseCommaSeparated(agent.target_source_id, true),
        target_status_id: this.parseCommaSeparated(agent.target_status_id, true),
        twilio_phone_number: agent.twilio_phone_number || "",
        agent_type: agent.agent_type || "interactive",
        run_interval_minutes: agent.run_interval_minutes !== undefined ? agent.run_interval_minutes : 60,
        schedule_type: agent.schedule_type || "interval",
        schedule_cron: agent.schedule_cron || "* * * * *",
        limit_to_department_id: this.parseCommaSeparated(agent.limit_to_department_id, true),
        limit_to_user_email: this.parseCommaSeparated(agent.limit_to_user_email, false),
        tools: Array.isArray(agent.tools) ? [...agent.tools] : [],
        extra_settings: (() => {
          const ext = JSON.parse(JSON.stringify(agent.extra_settings || {}));
          if (ext.daily_limit === undefined) {
            ext.daily_limit = "";
          }
          if (agent.agent_type === 'interactive') {
            if (!ext.fallback_templates) {
              ext.fallback_templates = { es: [""], en: [""] };
            }
            if (ext.max_outbound_sms === undefined) {
              ext.max_outbound_sms = 4;
            }
            if (ext.action_on_max_sms === undefined) {
              ext.action_on_max_sms = "stop_sending";
            }
            if (ext.action_status_id === undefined) {
              ext.action_status_id = null;
            }
          }
          return ext;
        })()
      };
      this.loadTargetOptions(this.editForm.target_type);
    },
    async toggleAgentStatus(agent, event) {
      const isChecked = event.target.checked;
      const data = {
        token: localStorage.getItem("jwt"),
        agent: {
          is_active: isChecked
        }
      };

      await axios
        .put(`/api/ia/agents/${agent.id}`, data)
        .then(() => {
          agent.is_active = isChecked;
          if (this.selectedAgent && this.selectedAgent.id === agent.id) {
            this.editForm.is_active = isChecked;
          }
          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true
          });
          Toast.fire({
            icon: "success",
            title: `${agent.display_name} ${isChecked ? 'enabled' : 'disabled'}`
          });
        })
        .catch(error => {
          console.error("Error toggling agent status:", error);
          event.target.checked = !isChecked; // revert
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          } else {
            Swal.fire("Error", "Could not toggle agent status.", "error");
          }
        });
    },
    copyWebhookUrl() {
      if (!this.webhookUrl) return;
      navigator.clipboard.writeText(this.webhookUrl).then(() => {
        Swal.fire({
          title: 'Copied!',
          text: 'Webhook URL copied to clipboard',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      });
    },
    async saveAgentSettings() {
      if (this.saving) return;
      this.saving = true;

      // Map detail endpoint matching standard pattern (ia/agents/<id> in Django)
      const data = {
        token: localStorage.getItem("jwt"),
        agent: {
          display_name: this.editForm.display_name,
          description: this.editForm.description,
          is_active: this.editForm.is_active,
          communication_mode: this.editForm.communication_mode,
          provider: this.editForm.provider,
          model_name: this.editForm.model_name,
          voice_id: this.editForm.voice_id,
          temperature: this.editForm.temperature,
          system_prompt: this.editForm.system_prompt,
          inbound_greeting: this.editForm.inbound_greeting,
          outbound_greeting: this.editForm.outbound_greeting,
          target_type: this.editForm.target_type,
          target_source_id: this.editForm.target_source_id && this.editForm.target_source_id.length > 0 ? this.editForm.target_source_id.join(',') : '',
          target_status_id: this.editForm.target_status_id && this.editForm.target_status_id.length > 0 ? this.editForm.target_status_id.join(',') : '',
          twilio_phone_number: this.editForm.twilio_phone_number,
          agent_type: this.editForm.agent_type,
          run_interval_minutes: this.editForm.run_interval_minutes,
          schedule_type: this.editForm.schedule_type,
          schedule_cron: this.editForm.schedule_cron,
          limit_to_department_id: this.editForm.limit_to_department_id && this.editForm.limit_to_department_id.length > 0 ? this.editForm.limit_to_department_id.join(',') : '',
          limit_to_user_email: this.editForm.limit_to_user_email && this.editForm.limit_to_user_email.length > 0 ? this.editForm.limit_to_user_email.join(',') : '',
          tools: this.editForm.tools || [],
          extra_settings: this.editForm.extra_settings
        }
      };

      await axios
        .put(`/api/ia/agents/${this.selectedAgent.id}`, data)
        .then(response => {
          this.saving = false;
          Swal.fire({
            icon: "success",
            title: "Settings Saved",
            text: `${this.selectedAgent.display_name} configuration updated successfully!`,
            timer: 2000,
            showConfirmButton: false
          });
          // Update local list
          const idx = this.agents.findIndex(a => a.id === this.selectedAgent.id);
          if (idx !== -1) {
            this.agents[idx] = { ...this.agents[idx], ...response.data };
            this.selectAgent(this.agents[idx]);
          }
        })
        .catch(error => {
          this.saving = false;
          console.error("Error saving agent settings:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          } else {
            Swal.fire("Error", "Failed to update agent settings.", "error");
          }
        });
    },
    addTemplateMessage() {
      if (this.editForm.extra_settings && this.editForm.extra_settings.templates) {
        this.editForm.extra_settings.templates.push("");
      }
    },
    removeTemplateMessage(idx) {
      if (this.editForm.extra_settings && this.editForm.extra_settings.templates) {
        this.editForm.extra_settings.templates.splice(idx, 1);
      }
    },
    addFallbackMessage(lang) {
      if (this.editForm.extra_settings && this.editForm.extra_settings.fallback_templates) {
        this.editForm.extra_settings.fallback_templates[lang].push("");
      }
    },
    removeFallbackMessage(lang, idx) {
      if (this.editForm.extra_settings && this.editForm.extra_settings.fallback_templates) {
        this.editForm.extra_settings.fallback_templates[lang].splice(idx, 1);
      }
    },
    onTargetTypeChange() {
      this.editForm.target_source_id = [];
      this.editForm.target_status_id = [];
      this.loadTargetOptions(this.editForm.target_type);
    },
    async loadTargetOptions(targetType) {
      if (!targetType) return;
      const data = {
        token: localStorage.getItem("jwt"),
        target_type: targetType
      };
      await axios
        .post("/api/ia/target-options", data)
        .then(response => {
          this.availableSources = response.data.sources || [];
          this.availableStatuses = response.data.statuses || [];
          this.availableDepartments = response.data.departments || [];
          this.availableRecruiters = response.data.recruiters || [];
        })
        .catch(error => {
          console.error("Error loading target options:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          }
        });
    },
    openTestChat() {
      this.testChatModalOpen = true;
      this.testMessages = [];
      this.testMessageInput = "";
    },
    closeTestChat() {
      this.testChatModalOpen = false;
    },
    async sendTestMessage() {
      if (!this.testMessageInput.trim() || this.testChatLoading) return;
      
      const userMsg = { role: 'user', content: this.testMessageInput.trim() };
      this.testMessages.push(userMsg);
      this.testMessageInput = "";
      this.testChatLoading = true;

      const data = {
        token: localStorage.getItem('jwt'),
        system_prompt: this.editForm.system_prompt,
        model_name: this.editForm.model_name,
        temperature: this.editForm.temperature,
        provider: this.editForm.provider,
        message: userMsg.content
      };

      await axios.post('/api/ia/simulate', data)
        .then(res => {
          if (res.data && res.data.reply) {
            this.testMessages.push({ role: 'ai', content: res.data.reply });
          }
        })
        .catch(error => {
          console.error("Test chat error:", error);
          this.testMessages.push({ role: 'ai', content: '⚠️ Error communicating with the server.' });
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          }
        })
        .finally(() => {
          this.testChatLoading = false;
        });
    }
  },
  mounted() {
    this.loadAgents();
    this.loadTools();
    this.loadAvailableModels();
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  }
};
</script>

<style scoped>
.transition-all {
  transition: all 0.25s ease-in-out;
}
.agent-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08) !important;
}
.bg-soft-primary-light {
  background-color: rgba(64, 81, 137, 0.05) !important;
}
.uppercase {
  text-transform: uppercase;
}
.text-truncate-two-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fade-in {
  animation: fadeIn 0.3s ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Custom Multi-select Dropdown styles */
.custom-multiselect-dropdown {
  position: relative;
}
.custom-multiselect-dropdown .form-select {
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 2.5rem;
}
.custom-multiselect-dropdown .form-select::after {
  content: "";
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #6c757d;
  pointer-events: none;
}
.custom-multiselect-dropdown .dropdown-menu {
  display: block;
  visibility: hidden;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: none;
}
.custom-multiselect-dropdown .dropdown-menu.show {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.custom-multiselect-dropdown .dropdown-item {
  transition: background-color 0.15s ease;
  user-select: none;
}
.custom-multiselect-dropdown .dropdown-item:hover {
  background-color: #f3f6f9;
}
.custom-multiselect-dropdown .dropdown-item input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  border-color: #ced4da;
}
.custom-multiselect-dropdown .dropdown-item input[type="checkbox"]:checked {
  background-color: #405189;
  border-color: #405189;
}
</style>
